import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { NotaToolbar } from './NotaToolbar';
import { FirmaBloque, FirmaImgsContext } from './FirmaBloque';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PX_MM = 96 / 25.4;
const GAP = 10; // separación visual entre hojas (mm)

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const contenidoInicialNota = () => {
  const d = new Date();
  return `<p style="text-align: right">Lomas de Zamora, ${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}</p><p></p><p><strong>A:</strong> </p><p></p><p></p>`;
};

const FONT_FACES = ['Regular:normal:normal', 'Bold:bold:normal', 'Italic:normal:italic', 'BoldItalic:bold:italic']
  .map(v => { const [n, w, s] = v.split(':'); return `@font-face{font-family:'NotaSerif';src:url('/fonts/LiberationSerif-${n}.ttf');font-weight:${w};font-style:${s};}`; })
  .join('');

export const NotaPaginadaEditor = ({ layout, css, initialHtml, onChange, selloSrc, firmaSrc }) => {
  const L = layout;
  const colsRef = useRef(null);
  const [paginas, setPaginas] = useState(1);
  const scrollRef = useRef(null);
  const [escala, setEscala] = useState(1);

  // En pantallas angostas la hoja se achica para entrar completa a lo ancho (la medición no cambia)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setEscala(Math.min(1, (el.clientWidth - 16) / (L.page_w * PX_MM))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [L]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] }, codeBlock: false, code: false, blockquote: false, horizontalRule: false, strike: false, link: false }),
      TextStyle, FontSize, FirmaBloque,
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
    ],
    content: initialHtml,
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  const medir = useCallback(() => {
    const el = colsRef.current;
    if (!el) return;
    const swMm = el.scrollWidth / PX_MM;
    const paso = L.page_w + GAP;
    setPaginas(Math.max(1, Math.round((swMm + paso - L.content_w) / paso)));
  }, [L]);

  useLayoutEffect(() => {
    if (!editor) return undefined;
    editor.on('update', medir);
    medir();
    document.fonts?.ready.then(medir);
    return () => editor.off('update', medir);
  }, [editor, medir]);

  useEffect(() => { if (editor) onChange(editor.getHTML()); }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  const paso = L.page_w + GAP;

  return (
    <div className="space-y-2 min-w-0" data-testid="nota-editor">
      <style>{FONT_FACES + css + '.nota-columns .ProseMirror{outline:none;white-space:pre-wrap;} .nota-columns .ProseMirror p.is-empty::before{content:none;}'}</style>
      <NotaToolbar editor={editor} paginas={paginas} />
      <div ref={scrollRef} className="overflow-x-auto bg-gray-200 rounded-lg p-2 sm:p-4" data-testid="nota-paginas-scroll">
        <div style={{ width: `${(paginas * paso - GAP) * escala}mm`, height: `${L.page_h * escala}mm` }}>
        <div style={{ position: 'relative', width: `${paginas * paso - GAP}mm`, height: `${L.page_h}mm`, transform: `scale(${escala})`, transformOrigin: '0 0' }}>
          {Array.from({ length: paginas }).map((_, i) => (
            <div key={i} data-testid={`nota-pagina-${i + 1}`} className="shadow-md"
              style={{
                position: 'absolute', left: `${i * paso}mm`, top: 0, width: `${L.page_w}mm`, height: `${L.page_h}mm`,
                background: i % 2 === 0 ? `#fff url(${API}/notas-libres/membrete.png) 0 0 / 100% 100% no-repeat` : '#fff',
              }}>
              <span className="absolute bottom-1 right-2 text-[10px] text-gray-400">{i + 1}</span>
            </div>
          ))}
          <div ref={colsRef} className="nota-columns" style={{
            position: 'absolute', left: `${L.margin_x}mm`, top: `${L.margin_top}mm`, width: `${L.content_w}mm`, height: `${L.content_h}mm`,
            columnWidth: `${L.content_w}mm`, columnGap: `${paso - L.content_w}mm`, columnFill: 'auto',
          }}>
            <FirmaImgsContext.Provider value={{ layout: L, selloSrc, firmaSrc }}>
              <div className="nota-doc"><EditorContent editor={editor} data-testid="nota-editor-contenido" /></div>
            </FirmaImgsContext.Provider>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
